<template>
    <div :class="props.class">
        <LoadingDialog v-if="savingCourse" />
        <h1 class="text-2xl font-bold mb-4">Formulário de Vídeo</h1>
        <form @submit.prevent="submitForm">
            <div v-if="courseData.categoryData && Array.isArray(courseData.categoryData) && courseData.categoryData.length > 0"
                class="my-3 flex gap-3">
                <Tag severity="info" class="px-3 py-1 font-medium bg-purple-suit-forced-light"
                    v-for="(category, index) of courseData.categoryData" :key="index">
                    {{ ucfirst(category.getTranslatedName()) }}
                </Tag>
            </div>
            <div class="mb-4">
                <label for="title" class="block text-sm font-medium text-gray-700">Título
                    <RequiredMarker :isValid="dataToValidate.title" />
                </label>
                <InputText id="title" v-model="courseData.title" required
                    class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500" />
            </div>
            <div class="mb-4">
                <label for="description" class="block text-sm font-medium text-gray-700">Descrição
                    <RequiredMarker :isValid="dataToValidate.description" />
                </label>
                <Textarea id="description" v-model="courseData.description" rows="4" required
                    class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500" />
            </div>
            <div class="mb-4">
                <label for="price" class="block text-sm font-medium text-gray-700">Valor
                    <RequiredMarker :isValid="dataToValidate.price" />
                </label>
                <InputNumber v-model="courseData.price" inputId="price" mode="currency" currency="BRL" locale="pt-BR"
                    class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    fluid />
            </div>
            <div class="mb-4">
                <label for="moduleId" class="block text-sm font-medium text-gray-700">Idioma do curso
                    <RequiredMarker :isValid="dataToValidate.courseLanguage" />
                </label>
                <Dropdown v-model="courseData.courseLanguage" :loading="languagesData.loading"
                    :options="languagesData.data" optionLabel="name"
                    class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500">
                    <template #value="slotProps">
                        <div v-if="slotProps.value">
                            {{ translate(slotProps.value.name, 'ucfirst') }}
                        </div>
                        <span v-else>
                            {{ translate('no language selected') }}
                        </span>
                    </template>

                    <template #option="slotProps">
                        <span>{{ translate(slotProps.option.name, 'ucfirst') }}</span>
                    </template>
                </Dropdown>
            </div>

            <div class="mb-4">
                <label for="moduleId" class="block text-sm font-medium text-gray-700">Idioma ensinado
                    <RequiredMarker :isValid="dataToValidate.teachedLanguage" />
                </label>
                <Dropdown v-model="courseData.teachedLanguage" :loading="languagesData.loading"
                    :options="languagesData.data" optionLabel="name"
                    class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500">
                    <template #value="slotProps">
                        <div v-if="slotProps.value">
                            {{ translate(slotProps.value.name, 'ucfirst') }}
                        </div>
                        <span v-else>
                            {{ translate('no language selected') }}
                        </span>
                    </template>

                    <template #option="slotProps">
                        <span>{{ translate(slotProps.option.name, 'ucfirst') }}</span>
                    </template>
                </Dropdown>
            </div>

            <div class="mb-4 flex gap-3">
                <div v-for="statusOpt in statusOptions" :key="statusOpt.key"
                    class="flex gap-2 text-secondary items-center">
                    <RadioButton v-model="courseData.active" :inputId="statusOpt.key" name="dynamic"
                        :value="statusOpt.key" />
                    <label :for="statusOpt.key">
                        {{ translate(statusOpt.name, 'ucfirst') }}
                    </label>
                </div>
            </div>

            <div class="mb-4">
                <label for="video" class="block text-sm font-medium text-gray-700">Capa do curso</label>
                <FileUpload mode="basic" accept="image/*" :chooseLabel="translate('select image').toUpperCase()"
                    :maxFileSize="2097152" @select="update" class="bg-carrot-forced" />
                <img v-if="courseData.coursePhoto" :src="courseData.coursePhoto" alt="Image"
                    :style="{ width: '320px!important', height: '180px!important' }"
                    class="shadow-md rounded mx-auto mt-5" />
                <div v-if="courseData.coursePhoto" class="text-center w-full mt-5">
                    <p class="rounded swadow p-2 bg-carrot text-white">
                        Isso é uma preview do arquivo final, não estranhe se a preview ficar desfocada. Qualquer arquivo
                        pode ser
                        enviado mas o mesmo será redimensionado para as proporções: 320 x 180!
                    </p>
                </div>
            </div>
            <div class="w-full text-center mt-20 border-t border-iron pt-10">
                <Button type="submit" class="px-4 py-2 bg-blue-600 text-white rounded-md no-bg-button"
                    @click.prevent="saveCourse()">Enviar</Button>
            </div>
        </form>
    </div>
</template>
<script setup>
import { defineProps, defineEmits, onMounted, reactive, ref, watch } from 'vue';
import Textarea from 'primevue/textarea';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Dropdown from 'primevue/dropdown';
import FileUpload from 'primevue/fileupload';
import { showToast } from '@/composables/toast';
import { isImageValid } from '@/composables/imageChecker';
import { useToast } from 'primevue/usetoast';
import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
import RequiredMarker from '@/components/misc/RequiredMarkerComponent.vue';
import { isTextValid, ucfirst } from '@/composables/textUtils';
import utils from '@/composables/utils';
import { translate } from '@/composables/translate';
import CourseService from '@/services/CourseService';
import RadioButton from 'primevue/radiobutton';
import { Tag } from 'primevue';
import LanguageService from '@/services/LanguageService';

const emit = defineEmits(["actionPerformed"]);
const toast = useToast();
const props = defineProps({
    courseData: {
        type: Object,
        required: true
    },
    class: {
        type: String,
        required: false,
        default: ''
    }
});

let savingCourse = ref(false);
let courseData = ref({
    uuid: null,
    title: null,
    description: null,
    timeInMinutes: null,
    courseLanguage: null,
    teachedLanguage: null,
    coursePhoto: null,
    price: null,
    active: 1,
    categoryData: []
});

const statusOptions = ref([
    { name: 'active', key: 1 },
    { name: 'inactive', key: 0 },
]);

onMounted(() => {
    setValues(props.courseData);
    listLanguages();
});

function emitSuccess() {
    emit("actionPerformed", true);
}

let languagesData = ref({
    data: [],
    loading: true
});
async function listLanguages() {
    languagesData.value.loading = true;
    const response = LanguageService.getLanguages();

    languagesData.value.data = [];
    languagesData.value.data = response.parsedItems();

    if (courseData.value.uuid != null) {
        setLanguages();
    }

    languagesData.value.loading = false;
}

watch(() => props.courseData, (newValue) => {
    setValues(newValue)
},
    { deep: true }
);

function setValues(values = {}) {
    for (let attr of Object.keys(courseData.value)) {
        let val = null;
        if (values[attr] != undefined)
            val = values[attr];
        courseData.value[attr] = val;
    }

    setLanguages();
}

function setLanguages() {
    for (let lang of languagesData.value.data) {
        if (courseData.value['courseLanguage']?.id == lang.id) {
            courseData.value['courseLanguage'] = lang;
        }

        if (courseData.value['teachedLanguage']?.id == lang.id) {
            courseData.value['teachedLanguage'] = lang;
        }
    }
}

let changedPhoto = ref(false);
async function update(event) {
    const files = event.files;
    files.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
            let response = isImageValid(file);
            if (!response.status) {
                showToast(toast, response.message, 'error');
                return;
            }
            courseData.value['coursePhoto'] = e.target.result;
            changedPhoto.value = true
        };
        reader.readAsDataURL(file);
    });
}

let dataToValidate = reactive({
    title: true,
    description: true,
    price: true,
    courseLanguage: true,
    teachedLanguage: true,
})
let errorMessages = {
    title: 'Por favor informe o titulo do curso',
    description: 'Por favor informe a descrição do curso',
    price: 'Por favor informe o preço do curso',
    courseLanguage: 'Por favor informe o idioma do curso',
    teachedLanguage: 'Por favor informe o idioma ensinado',
}
async function saveCourse() {
    let isValid = true;
    for (let attr of Object.keys(dataToValidate)) {
        if (!isTextValid(courseData.value[attr])) {
            isValid = false;
            dataToValidate[attr] = false;
            showToast(toast, errorMessages[attr], 'error');
            break;
        } else {
            dataToValidate[attr] = true;
        }
    }
    if (!isValid) {
        return;
    }

    let dataToSave = utils.copyVariable(courseData.value);
    for (let attr of ['courseLanguage', 'teachedLanguage']) {
        if (dataToSave[attr] != null)
            dataToSave[attr] = dataToSave[attr].id;
    }
    let coursePhoto = null;
    if (changedPhoto.value) {
        coursePhoto = dataToSave['coursePhoto'];
    }
    dataToSave['coursePhoto'] = coursePhoto;
    let newCourse = dataToSave.uuid ? false : true;

    savingCourse.value = false;
    const response = await CourseService.saveCourse(dataToSave, dataToSave.uuid);
    savingCourse.value = true;

    if (!response.success()) {
        showToast(toast, response.getMessage(), 'error');
        return;
    }

    courseData.value['uuid'] = response.getContent().uuid;
    const successMessage = newCourse ? translate('course created', 'ucfirst') : translate('course updated', 'ucfirst');
    showToast(toast, successMessage);

    emitSuccess();
}
</script>